import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getNurse } from '../../api/dashboard';
import { useAuth, useIsAdmin } from '../../auth/AuthContext';
import { errorCode } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import StatusMessage from '../../components/StatusMessage';
import { formatDateTime } from '../../utils/format';
import { adminErrorMessage } from './adminErrors';
import PhoneApp from '../../components/PhoneApp';
import NurseActivity from './NurseActivity';
import './Admin.css';

const NurseDetail = () => {
  const { nurseId } = useParams();
  const navigate = useNavigate();
  // Admins never see individual report sheets, so their nurse page is the
  // profile plus aggregate activity (NurseActivity). Charge nurses also see the nurse's phone: the app's home
  // screen with the patients this nurse sees; tapping one opens its report
  // sheet in the same phone, like the app.
  const isAdmin = useIsAdmin();
  const myUnitName = useAuth()?.nurse?.unit?.name || 'your unit';
  const [nurse, setNurse] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setNurse(null);
    try {
      setNurse(await getNurse(nurseId));
    } catch (err) {
      setError(errorCode(err) === 'not_found' ? 'not_found' : adminErrorMessage(err, "We couldn't load this nurse."));
    }
  }, [nurseId]);

  useEffect(() => {
    load();
  }, [load]);

  const back = (
    <button type="button" className="back-button" onClick={() => navigate('/nurses')}>← Nurses</button>
  );

  if (error || !nurse) {
    return (
      <>
        <PageHeader title="Nurse" before={back} />
        <div className="component-container">
          <div className="admin-content">
            {error === 'not_found' && <StatusMessage kind="error">This nurse isn't in your organization.</StatusMessage>}
            {error && error !== 'not_found' && <StatusMessage kind="error" onRetry={load}>{error}</StatusMessage>}
            {!error && <StatusMessage kind="loading">Loading nurse…</StatusMessage>}
          </div>
        </div>
      </>
    );
  }

  const lastLogin = (nurse.hasLoggedIn && formatDateTime(nurse.lastLoginAt)) || 'Never';
  // The nurse's patients in rooms outside the charge nurse's unit: counted,
  // not listed (they're another unit's patients), and flagged since a nurse's
  // patients should always be on their own unit.
  const offUnit = Number.isInteger(nurse.offUnitSheetCount) ? nurse.offUnitSheetCount : 0;

  return (
    <>
      <PageHeader
        title={nurse.name || nurse.email}
        before={back}
        after={(
          <>
            {nurse.unit && <span className="unit-chip">{nurse.unit.name}</span>}
          </>
        )}
      />
      <div className={`component-container${isAdmin ? '' : ' phone-page nurse-phone-page'}`}>
        <div className={isAdmin ? 'detail-content' : 'nurse-profile-strip'}>
          <div className="list-card">
            <div className="list-card-main">
              <span className="field-label">Email</span>
              <span>{nurse.email}</span>
            </div>
            <div className="list-card-main">
              <span className="field-label">Unit</span>
              <span>{nurse.unit?.name || 'No unit'}</span>
            </div>
            <div className="list-card-main">
              <span className="field-label">Last login</span>
              <span>{lastLogin}</span>
            </div>
          </div>
          {isAdmin && <NurseActivity nurseId={nurse.id || nurseId} firstName={nurse.firstName} />}
        </div>
        {!isAdmin && (
          <PhoneApp
            firstName={nurse.firstName}
            nurseName={nurse.name}
            sheets={nurse.reportSheets || []}
            label={`${nurse.name || 'This nurse'}'s patients as shown in the Aren app`}
            caption={offUnit > 0 && (
              <span className="off-unit-caption" role="note">
                {offUnit} of {nurse.firstName || nurse.name || 'this nurse'}'s patients {offUnit === 1 ? 'is' : 'are'} in a
                room outside {myUnitName}, so {offUnit === 1 ? "it isn't" : "they aren't"} shown here. Ask an admin to fix the
                patient's room or the nurse's unit.
              </span>
            )}
          />
        )}
      </div>
    </>
  );
};

export default NurseDetail;
